/**
 * The built-in demo dossiers, as a section of the workspace screen.
 *
 * M1 had this as the whole start screen. M2 makes it one section among the
 * workspace's own dossiers, and the run it starts now names the workspace —
 * `POST /runs` needs it for a founder, who sees more than one.
 */
import { useState } from 'react'
import { FileText, Play } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n } from '@/i18n'
import { bautypLabel, verfahrenLabel } from '@/i18n/enumLabels'
import { useAsync } from '@/lib/useAsync'
import type { DemoDossier, Run } from '@/api/types'

export function DemoDossiers({
  workspaceId,
  onRunStarted,
}: {
  workspaceId?: string
  onRunStarted: (run: Run) => void
}) {
  const { t } = useI18n()
  const dossiers = useAsync<DemoDossier[]>((signal) => api.demoDossiers(signal))
  const [starting, setStarting] = useState<string | null>(null)
  const [startError, setStartError] = useState<string | null>(null)

  const start = async (dossierId: string) => {
    setStarting(dossierId)
    setStartError(null)
    try {
      if (workspaceId) {
        // Take the built-in case INTO the workspace first, then run that row.
        //
        // Running a built-in fixture directly produces a run that belongs to no
        // dossier — and a run with no dossier has no facts to review, no place
        // to record a correction and nothing to re-check. Adopting first is what
        // makes the demo case a full citizen: it appears in the workspace's
        // dossier list, the facts surface opens on it, and a correction can be
        // re-checked and diffed like any uploaded dossier.
        const dossier = await api.adoptDemoDossier(workspaceId, dossierId)
        onRunStarted(await api.createRun(dossier.id, workspaceId))
      } else {
        onRunStarted(await api.createRun(dossierId))
      }
    } catch (cause) {
      setStartError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setStarting(null)
    }
  }

  return (
    <section aria-labelledby="dossiers-heading" className="flex flex-col gap-4">
      <div>
        <h2 id="dossiers-heading" className="font-display text-[21px] font-semibold">
          {t('start.dossiersTitle')}
        </h2>
        <p className="mt-1 text-[13px] text-muted">{t('start.dossiersLead')}</p>
      </div>

      {dossiers.loading ? <p className="text-[13px] text-muted">{t('common.loading')}</p> : null}

      {dossiers.error ? (
        <ErrorNote message={dossiers.error} onRetry={dossiers.reload} retryLabel={t('common.retry')} />
      ) : null}

      {startError ? <ErrorNote message={startError} /> : null}

      {dossiers.data?.length === 0 ? (
        <p className="text-[13px] text-muted">{t('start.dossiersEmpty')}</p>
      ) : null}

      <ul className="grid gap-4 md:grid-cols-2">
        {dossiers.data?.map((dossier) => (
          <li key={dossier.id}>
            <Card className="flex h-full flex-col">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <Icon icon={FileText} className="mt-1 size-5 text-accent" />
                  <div className="flex flex-col gap-1">
                    <CardTitle>{dossier.title}</CardTitle>
                    <CardDescription>
                      <span className="num">{dossier.gemeinde}</span> ·{' '}
                      <span className="num">{dossier.zone}</span>
                      {dossier.bautyp ? <> · {bautypLabel(dossier.bautyp, t)}</> : null}
                      {dossier.verfahren ? <> · {verfahrenLabel(dossier.verfahren, t)}</> : null}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="mt-auto flex flex-col gap-4">
                {dossier.note ? <p className="text-[13px] text-muted">{dossier.note}</p> : null}
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="outline">{t('start.synthetic')}</Badge>
                  <code className="num text-[12px] text-muted-2">{dossier.id}</code>
                </div>
                <Button
                  onClick={() => void start(dossier.id)}
                  disabled={starting !== null}
                  className="self-start"
                >
                  <Icon icon={Play} />
                  {starting === dossier.id ? t('start.starting') : t('start.startRun')}
                </Button>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
    </section>
  )
}
